import { useState } from "react";
import { getUserEmail, getGroups, clearToken } from "../lib/auth.js";
import { refreshTokens, isCognitoConfigured, revokeToken } from "../lib/cognito.js";

interface Props {
  onCheckAgain: () => void;
  onSignOut: () => void;
}

export function PendingApprovalScreen({ onCheckAgain, onSignOut }: Props) {
  const email = getUserEmail();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCheckAgain() {
    setChecking(true);
    setError(null);
    try {
      const ok = await refreshTokens();
      if (!ok) {
        setError("Session lost. Please sign in again.");
        return;
      }
      const groups = getGroups();
      if (groups.length > 0) {
        onCheckAgain();
      } else {
        setError("Your account is still pending approval.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh session.");
    } finally {
      setChecking(false);
    }
  }

  async function handleSignOut() {
    if (isCognitoConfigured()) revokeToken();
    clearToken();
    onSignOut();
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
      <div className="w-full max-w-sm space-y-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm text-center">
        <div>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
            <svg className="h-7 w-7 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Pending Approval
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Your account{email ? ` (${email})` : ""} is waiting for administrator approval before you can access the application.
          </p>
        </div>

        {error && (
          <p className="rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

        <div className="space-y-3">
          <button
            onClick={handleCheckAgain}
            disabled={checking}
            className="w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {checking ? "Checking…" : "Check again"}
          </button>

          <button
            onClick={handleSignOut}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Sign out
          </button>
        </div>

        <p className="text-xs text-gray-400 dark:text-gray-600">
          Contact the administrator if you believe this is a mistake.
        </p>
      </div>
    </div>
  );
}
